import React, { useMemo, useState } from 'react';
import { GlobeIcon, CheckIcon, InfoIcon } from '../icons';
import { FormattedMessage } from '../../../../util/reactIntl';
import { useParams } from 'react-router-dom';
import BulletPointsFormatter from '../../../../util/formatters/bulletPointsFormatter/bulletPointsFormatter';
import Modal from '../Utils/Modal/Modal';
import css from './OnSiteTabs.module.css';

const FALLBACK_GERMAN = 'Übersetzung hinzufügen';
const PREVIEW_CHAR_LIMIT = 350;

const ENGLISH_NAMES = [
  'english',
  'spanish',
  'french',
  'german',
  'portuguese',
  'chinese',
  'italian',
  'dutch',
  'arabic',
];
const GERMAN_NAMES = [
  'Englisch',
  'Spanisch',
  'Französisch',
  'Deutsch',
  'Portugiesisch',
  'Chinesisch',
  'Italienisch',
  'Niederländisch',
  'Arabisch',
];

const TABS = [
  { id: 'languages', labelId: 'EditListingDescriptionForm.languages' },
  { id: 'packList', labelId: 'EditListingDescriptionForm.packlist' },
  { id: 'houserules', labelId: 'EditListingDescriptionForm.houserules' },
];

const LanguageIcon = () => (
  <GlobeIcon className={css.languageIcon} size={18} aria-hidden="true" />
);

const PackListIcon = () => (
  <CheckIcon className={css.itemIcon} size={18} aria-hidden="true" />
);

const HouseRulesIcon = () => (
  <InfoIcon className={css.itemIcon} size={18} aria-hidden="true" />
);

const parseListItems = text => {
  if (!text || typeof text !== 'string') return [];
  return text
    .split(/\n+/)
    .map(line => line.replace(/^[\s•\-*–\d.)]+/, '').trim())
    .filter(Boolean);
};

const getLocalizedText = (enValue, deValue, lang) => {
  if (lang === 'de' && deValue && deValue !== FALLBACK_GERMAN) return deValue;
  if (enValue) return enValue;
  if (deValue && deValue !== FALLBACK_GERMAN) return deValue;
  return '';
};

const normalizeLanguageName = (name, lang) => {
  if (!name) return '';
  const lower = String(name).toLowerCase().trim();
  const index = ENGLISH_NAMES.indexOf(lower);
  if (index !== -1) {
    return lang === 'de' ? GERMAN_NAMES[index] : ENGLISH_NAMES[index].charAt(0).toUpperCase() + ENGLISH_NAMES[index].slice(1);
  }
  return String(name).trim();
};

const parseOtherLanguages = (otherLanguages, lang) => {
  if (!otherLanguages) return [];

  const values = Array.isArray(otherLanguages)
    ? otherLanguages
    : String(otherLanguages)
        .split(/[,;|/]/)
        .map(item => item.trim())
        .filter(Boolean);

  return values.map(value => normalizeLanguageName(value, lang)).filter(Boolean);
};

const getAllLanguages = (languages, otherLanguages, lang) => {
  const primary = Array.isArray(languages)
    ? languages.map(name => normalizeLanguageName(name, lang))
    : [];
  const additional = parseOtherLanguages(otherLanguages, lang);
  return [...new Set([...primary, ...additional].filter(Boolean))];
};

const GridItems = ({ items, icon: Icon, onItemClick }) => {
  if (!items.length) return null;
  return (
    <div className={css.itemGrid}>
      {items.map((item, index) => (
        <button
          key={index}
          type="button"
          className={css.gridItem}
          onClick={() => onItemClick?.(item)}
        >
          <Icon />
          <span>{item}</span>
        </button>
      ))}
    </div>
  );
};

const ContentModal = ({ isOpen, onClose, titleId, fullText, items, icon }) => (
  isOpen ? (
    <Modal isOpen={isOpen} onClose={onClose}>
      <h2>
        <FormattedMessage id={titleId} />
      </h2>
      <div className={css.modalContent}>
        {items?.length ? (
          <GridItems items={items} icon={icon} />
        ) : (
          <BulletPointsFormatter text={fullText} />
        )}
      </div>
    </Modal>
  ) : null
);

const LongTextPanel = ({ titleId, fullText, previewText, icon: Icon }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const previewItems = parseListItems(previewText);
  const fullItems = parseListItems(fullText);

  if (!fullText) {
    return <p className={css.emptyState}>N/A</p>;
  }

  const useGrid = fullItems.length > 0;
  const openModal = () => setIsModalOpen(true);

  return (
    <>
      {useGrid ? (
        <GridItems
          items={previewItems.length ? previewItems : fullItems.slice(0, 4)}
          icon={Icon}
          onItemClick={openModal}
        />
      ) : (
        <button type="button" className={css.fallbackContentButton} onClick={openModal}>
          <BulletPointsFormatter text={previewText || fullText} />
        </button>
      )}
      <button type="button" className={css.showMoreBtn} onClick={openModal}>
        <FormattedMessage id="EditListingOffersForm.activities.showMore" />
      </button>
      <ContentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        titleId={titleId}
        fullText={fullText}
        items={fullItems}
        icon={Icon}
      />
    </>
  );
};

const OnSiteTabs = ({ publicData = {} }) => {
  const { lang } = useParams();
  const [selectedTab, setSelectedTab] = useState('languages');
  const [packListModalOpen, setPackListModalOpen] = useState(false);
  const [houseRulesModalOpen, setHouseRulesModalOpen] = useState(false);

  const languages = publicData.languages || [];
  const otherLanguages = getLocalizedText(
    publicData.otherLanguages,
    publicData.otherLanguages_de,
    lang
  );

  const packListText = getLocalizedText(publicData.packlist, publicData.packlist_de, lang);
  const houseRulesText = getLocalizedText(publicData.houserules, publicData.houserules_de, lang);

  const allLanguages = useMemo(
    () => getAllLanguages(languages, otherLanguages, lang),
    [languages, otherLanguages, lang]
  );

  const packListItems = useMemo(() => parseListItems(packListText), [packListText]);
  const houseRulesItems = useMemo(() => parseListItems(houseRulesText), [houseRulesText]);

  const shortPackList =
    typeof packListText === 'string' ? packListText.substring(0, PREVIEW_CHAR_LIMIT) : '';
  const shortHouseRules =
    typeof houseRulesText === 'string' ? houseRulesText.substring(0, PREVIEW_CHAR_LIMIT) : '';

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'languages':
        if (!allLanguages.length) {
          return <p className={css.emptyState}>N/A</p>;
        }
        return (
          <div className={css.languagePills}>
            {allLanguages.map((language, index) => (
              <span key={`${language}-${index}`} className={css.languagePill}>
                <LanguageIcon />
                {language}
              </span>
            ))}
          </div>
        );

      case 'packList':
        if (!packListText) return <p className={css.emptyState}>N/A</p>;
        if (packListText.length > PREVIEW_CHAR_LIMIT) {
          return (
            <LongTextPanel
              titleId="EditListingDescriptionForm.packlist"
              fullText={packListText}
              previewText={shortPackList}
              icon={PackListIcon}
            />
          );
        }
        if (packListItems.length) {
          return (
            <>
              <GridItems
                items={packListItems}
                icon={PackListIcon}
                onItemClick={() => setPackListModalOpen(true)}
              />
              <ContentModal
                isOpen={packListModalOpen}
                onClose={() => setPackListModalOpen(false)}
                titleId="EditListingDescriptionForm.packlist"
                fullText={packListText}
                items={packListItems}
                icon={PackListIcon}
              />
            </>
          );
        }
        return (
          <>
            <button
              type="button"
              className={css.fallbackContentButton}
              onClick={() => setPackListModalOpen(true)}
            >
              <BulletPointsFormatter text={packListText} />
            </button>
            <ContentModal
              isOpen={packListModalOpen}
              onClose={() => setPackListModalOpen(false)}
              titleId="EditListingDescriptionForm.packlist"
              fullText={packListText}
              items={packListItems}
              icon={PackListIcon}
            />
          </>
        );

      case 'houserules':
        if (!houseRulesText) return <p className={css.emptyState}>N/A</p>;
        if (houseRulesText.length > PREVIEW_CHAR_LIMIT) {
          return (
            <LongTextPanel
              titleId="EditListingDescriptionForm.houserules"
              fullText={houseRulesText}
              previewText={shortHouseRules}
              icon={HouseRulesIcon}
            />
          );
        }
        if (houseRulesItems.length) {
          return (
            <>
              <GridItems
                items={houseRulesItems}
                icon={HouseRulesIcon}
                onItemClick={() => setHouseRulesModalOpen(true)}
              />
              <ContentModal
                isOpen={houseRulesModalOpen}
                onClose={() => setHouseRulesModalOpen(false)}
                titleId="EditListingDescriptionForm.houserules"
                fullText={houseRulesText}
                items={houseRulesItems}
                icon={HouseRulesIcon}
              />
            </>
          );
        }
        return (
          <>
            <button
              type="button"
              className={css.fallbackContentButton}
              onClick={() => setHouseRulesModalOpen(true)}
            >
              <BulletPointsFormatter text={houseRulesText} />
            </button>
            <ContentModal
              isOpen={houseRulesModalOpen}
              onClose={() => setHouseRulesModalOpen(false)}
              titleId="EditListingDescriptionForm.houserules"
              fullText={houseRulesText}
              items={houseRulesItems}
              icon={HouseRulesIcon}
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <section className={css.section} data-elbaction="visible:vis onsite">
      <h2 className={css.title}>
        <FormattedMessage id="ListingPage.onsite" />
      </h2>

      <div className={css.tabBar} role="tablist">
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selectedTab === tab.id}
            className={selectedTab === tab.id ? css.tabActive : css.tab}
            onClick={() => setSelectedTab(tab.id)}
            data-elbaction={`click:click ${tab.id}`}
          >
            <FormattedMessage id={tab.labelId} />
          </button>
        ))}
      </div>

      <div className={css.tabPanel} role="tabpanel">
        {renderTabContent()}
      </div>
    </section>
  );
};

export default OnSiteTabs;
